import '../../lang/i18n';
import React, {useState, useEffect} from "react";
import { useTranslation } from 'react-i18next';
import Api from "../../services/Api";
import CustomButton from "../../components/CustomButton";
import { TrashBin as TrashIcon } from "flowbite-react-icons/outline";
import { GoogleCalendarIcon, GoogleIcon, MicrosoftCalendarIcon } from "../../components/Icons/AvailabilityBrandIcons";
import {CONFIG} from "../../constants";
import {getLanguage} from "../../lang/helpers";
import AlertIcon from "../../components/Icons/AlertIcon";

interface SyncedCalendarInterface {
  id: number
  name: string
  email: string
  hasRefreshToken: boolean
}

export default function AvailabilitySync() {

  const api = Api.getInstance();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language)
  const [calendars, setCalendars] = useState<Array<SyncedCalendarInterface>>([]);

  useEffect(() => {
    getData()
  }, [])

  const getData = () => {
    api.get('synced_calendars/get').then((result) => {
      setCalendars(result.json.calendars)
    })
  }

  const getEvents = (calendarId: number) => {
    api.get('synced_calendars/events/' + calendarId).then((result) => {
      console.log(result.json.events)
    })
  }

  const deleteCalendar = (id: number) => {
    api.get('synced_calendars/delete/' + id).then(() => {
      getData()
    })
  }

  const showEventualErrors = () => {
    for (const calendar of calendars) {
      if (!calendar.hasRefreshToken && calendar.name === 'Google') {
        return (
          <div className="mt-4 p-4 bg-neutral-700 border rounded-md border-cherry-500">
            <div className="flex items-center">
              <AlertIcon aria-label="Error" className="text-cherry-500!"/>
              <div className="ml-2">
                <p className="font-medium">{lang === 'sv' ? 'Synkning misslyckades' : 'Sync failed'}</p>
              </div>
            </div>

            <div className="ml-8 mt-4">
              <ol>
                <li>1.&nbsp;
                  {lang === 'sv' ?
                    'Gå till Googles sida (nedan) och klicka på "CoachApp".'
                    :
                    'Go to the Google page (below) and click "CoachApp".'
                  }
                </li>
                <li>2.&nbsp;
                  {lang === 'sv' ?
                    'Klicka "Radera alla kopplingar till CoachApp".'
                    :
                    'Click "Delete all connections to CoachApp".'
                  }
                </li>
                <li>3.&nbsp;
                  {lang === 'sv' ?
                    'Kom tillbaka till denna sida, ta bort Google och lägg till den igen.'
                    :
                    'Come back to this page, remove Google and add it again.'
                  }
                </li>
              </ol>
              <p className="mt-4">
                <a href="https://myaccount.google.com/connections?filters=3,4" className="underline">
                  {lang === 'sv' ? 'Googles appar och tjänster från tredjepart' : 'Google apps and services from third part'}
                </a>
              </p>
            </div>

          </div>
        )
      }
    }
  }

  const showSyncedCalendars = () => {
    if (calendars.length === 0) {
      return (
        <></>
      )
    } else {
      return (
        <>
          <div className="text-lg font-medium mt-4">
            {t('synced-calendars')}
          </div>
          {
            showEventualErrors()
          }
          {
            calendars.map((calendar, index) => {
              const CalendarIcon = calendar.name === "Microsoft" ? MicrosoftCalendarIcon : GoogleCalendarIcon
              return (
                <div key={"synced_calendar_" + index} className="mt-4 flex justify-between items-center">
                  <div className="w-1/3 flex items-center">
                    <CalendarIcon alt="Cal" onClick={() => getEvents(calendar.id)}/>
                    <div className="ml-4 text-sm">
                      <div className="font-medium">{calendar.name}</div>
                      <div>{calendar.email}</div>
                    </div>
                  </div>

                  <div className="z-box">
                    <TrashIcon className="h-4 w-4" aria-hidden="true"
                               onClick={() => deleteCalendar(calendar.id)} />
                  </div>
                </div>
              )
            })
          }
        </>
      )
    }
  }

  const syncMicrosoft = () => {
    console.log('syncMicrosoft')
    const clientId = 'bf280ab1-e7b1-4826-bbc0-2b73bb3bf11d'
    const redirect = CONFIG.APP_HOST + 'availability'
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${redirect}&response_type=code&scope=Calendars.Read%20Calendars.Read.Shared%20offline_access&response_mode=query&state=Microsoft12`
  }

  const syncGoogle = () => {
    // https://developers.google.com/identity/protocols/oauth2/web-server#httprest_3
    console.log('syncGoogle')
    const clientId = '1083297605583-h136trshd252u8ts8l1frh3p6nmsoj3s.apps.googleusercontent.com'
    const redirect = CONFIG.APP_HOST + 'availability'
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirect}&response_type=code&scope=https%3A//www.googleapis.com/auth/calendar.events.freebusy&access_type=offline`
  }

  const hasSyncedCalendar = (name: string) => {
    for (const calendar of calendars) {
      if (calendar.name === name) {
        return true
      }
    }
    return false
  }

  const showAddCalendars = () => {
    return (
      <>
        <div className="mt-4 text-lg font-medium">
          {
            calendars.length === 0 ?
              lang === 'sv' ? "Välj kalender att synka med" : "Choose calendar to sync with"
                : lang === 'sv'  ? "Lägg till kalender" : "Add calendar"
          }
        </div>
        <div className="mt-4 bg-neutral-700 rounded p-4">
          {
            !hasSyncedCalendar("Google") &&
              <>
                  <div className="font-medium">Google</div>
                  <hr className="border-gray-500 mt-2"/>
                  <div className="flex my-4 items-center">
                      <div className="w-1/3 flex items-center">
                          <GoogleIcon alt="Google"/>
                          <div className="ml-4 text-sm font-medium"></div>
                      </div>
                      <div className="w-1/3 text-sm text-neutral-400">Gmail, Gsuite</div>
                      <CustomButton className="w-1/3" buttonType="outlineWhiteBase"
                                    onClick={() => syncGoogle()}>Synka</CustomButton>
                  </div>
              </>
          }

          {
            !hasSyncedCalendar("Microsoft") &&
            <>
              <div className="font-medium">Microsoft</div>
              <hr className="border-gray-500 mt-2"/>
              <div className="flex mt-4 items-center">
                  <div className="w-1/3 flex items-center">
                      <MicrosoftCalendarIcon alt="microsoft"/>
                      <div className="ml-4 text-sm font-medium"></div>
                  </div>
                  <div className="w-1/3 text-sm text-neutral-400">
                      Office 365, Outlook.com, live.com or hotmail calendar
                  </div>
                  <CustomButton className="w-1/3" buttonType="outlineWhiteBase"
                                onClick={() => syncMicrosoft()}>Synka</CustomButton>
              </div>
            </>
          }

        </div>
      </>
    )
  }

  return (
    <>
      {showSyncedCalendars()}
      {calendars.length < 2 && showAddCalendars()}
    </>
  )
};